import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { INotification } from '../common/types';
import { AppDispatch, RootState } from '../store';
import { getReleaseNotes, getUserGuide } from '../services/apiEndpoints';
import { IGuideData } from '../types';

interface IUserGuidePayload {
    isAdmin: boolean;
    view: string;
}
export const fetchChatUserGuide = createAsyncThunk(
    'user/fetchChatUserGuide',
    async (payload: IUserGuidePayload, thunkAPI) => {
        const queryParams = new URLSearchParams({
            isAdmin: payload.isAdmin.toString(),
            view: payload.view,
        });
        try {
            const response = await getUserGuide(queryParams);
            return response;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : "An unknown error occurred";
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const fetchWorkflowUserGuide = createAsyncThunk(
    'user/fetchWorkflowUserGuide',
    async (payload: IUserGuidePayload, thunkAPI) => {
        const queryParams = new URLSearchParams({
            isAdmin: payload.isAdmin.toString(),
            view: payload.view,
        });
        try {
            const response = await getUserGuide(queryParams);
            return response;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : "An unknown error occurred";
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const fetchSupportInfo = createAsyncThunk(
    'user/fetchSupportInfo',
    async (payload: IUserGuidePayload, thunkAPI) => {
        const queryParams = new URLSearchParams({
            isAdmin: payload.isAdmin.toString(),
            view: payload.view,
        });
        try {
            const response = await getUserGuide(queryParams);
            return response;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : "An unknown error occurred";
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const fetchReleaseNotes = createAsyncThunk(
    'user/fetchReleaseNotes',
    async (_, thunkAPI) => {
        try {
            const response = await getReleaseNotes();
            return response;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : "An unknown error occurred";
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

interface IAppInfoState {
    notifications: INotification[];
    userGuide: IGuideData;
    releaseNotes: Object;
}

const initialState: IAppInfoState = {
    notifications: [],
    userGuide: {
        chat: [],
        workflow: [],
        support: []
    },
    releaseNotes: {}
};

const appInfoSlice = createSlice({
    name: 'app-info',
    initialState,
    reducers: {
        addNotificationToIndex(state, action: PayloadAction<{ notification: Omit<INotification, 'isRead'>; index?: number }>) {
            const newNotification: INotification = {
                ...action.payload.notification,
                isRead: false,
                ...(action.payload.notification.linkConfig && { 
                    linkConfig: action.payload.notification.linkConfig 
                })
            };
            if (state.notifications) {
                if (action.payload.index !== undefined) {
                    state.notifications[action.payload.index] = newNotification;
                } else {
                    state.notifications.push(newNotification);
                }
            } else {
                state.notifications = [newNotification];
            }
        },
        setReadNotifications(state) {
            state.notifications?.forEach(notification => (notification.isRead = true));
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchChatUserGuide.fulfilled, (state, action) => {
                state.userGuide.chat = action.payload.data;
            })
            .addCase(fetchWorkflowUserGuide.fulfilled, (state, action) => {
                state.userGuide.workflow = action.payload.data;
            })
            .addCase(fetchSupportInfo.fulfilled, (state, action) => {
                state.userGuide.support = action.payload.data;
            })
            .addCase(fetchReleaseNotes.fulfilled, (state, action) => {
                state.releaseNotes = action.payload.data;
            })
    },
});

export const addNotification = (notification: INotification, index?: number) => 
    (dispatch: AppDispatch, getState: () => RootState): number => {
        if (index !== undefined) {
            dispatch(appInfoSlice.actions.addNotificationToIndex({notification, index}));
            return index;  // if index is provided, return as-is
        } else {
            const currentLength = getState().appInfo.notifications?.length || 0;
            dispatch(appInfoSlice.actions.addNotificationToIndex({notification}));
            return currentLength;  // if adding, return the new item’s index
        }
};

export const getAndReadNotifications = () =>
    (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(appInfoSlice.actions.setReadNotifications());

        return getState().appInfo.notifications;
};

export const { addNotificationToIndex, setReadNotifications } = appInfoSlice.actions
export default appInfoSlice.reducer;